/**
 * Finance → Control Presupuestario → P&L: el modelo de cálculo, puro.
 *
 * Toma el dataset que arma el backend (`PnlDashboardData`, importes en USD) y
 * devuelve lo que dibuja cada bloque del dashboard: KPIs, waterfall de EBITDA,
 * drivers, FX, COGS por Solución, Investment por business case, tabla y heatmap.
 * Sin imports de runtime: lo usa el front, el digest del Brain (brain-areas.ts)
 * y lo testea el backend (`finance-pnl.test.ts`).
 *
 * Convenciones (vienen del dashboard original de Finance):
 *  - Favorable / desfavorable, no + / −: OPEX por debajo del BGT es buena noticia
 *    y EBITDA por debajo es mala. Cada línea declara su dirección deseada (`up`).
 *  - El waterfall siempre va de EBITDA BGT a EBITDA real; cada barra es
 *    "mejora / deteriora EBITDA", no "buena / mala noticia".
 *  - COGS e Investment: el orden es siempre por dólares, nunca por porcentaje.
 *  - Payroll: una sola categoría agregada ('S&B') por centro de costo, nunca por
 *    rol. Non-payroll + payroll = la línea del P&L, en el real y en el plan.
 */

import type {
  PnlCohort,
  PnlCostLine,
  PnlDashboardData,
  PnlLine,
  PnlMonthLines,
  PnlPayKind,
  PnlScenario,
} from './types/finance-pnl';

export type PnlPeriod = 'month' | 'ytd';
export type PnlBase = 'budget' | 'prev';
export type PnlDriverGroup = 'revenue' | 'cogs' | 'gya' | 'investment';
export type PnlBreakdownPeriod = 'month' | 'ytd' | 'semester' | 'year';
/** Qué parte de una línea de costo se mira: solo non-payroll (default), solo payroll, o las dos sumadas. */
export type PnlPayMode = PnlPayKind | 'total';

/** Las subcategorías de un driver se recortan a los movimientos más grandes; el total del padre suma todas. */
export const DRIVER_SUB_LIMIT = 8;

export const PNL_LINES: PnlLine[] = ['revenue', 'cogs', 'gya', 'investment', 'bonos', 'taxes'];
export const PNL_COST_LINES: PnlCostLine[] = ['cogs', 'gya', 'investment'];

export function emptyLines(month: number): PnlMonthLines {
  return { month, revenue: 0, cogs: 0, gya: 0, investment: 0, bonos: 0, taxes: 0 };
}

export function opexOf(l: PnlMonthLines): number {
  return l.cogs + l.gya + l.investment + l.bonos + l.taxes;
}

export function ebitdaOf(l: PnlMonthLines): number {
  return l.revenue - opexOf(l);
}

/**
 * Gross Profit = Revenue − COGS − Taxes (Finance 07/10/2026: Taxes va debajo de COGS y
 * entra en el margen bruto; con el cierre de agosto da 623,3k real y 1.091,1k BGT).
 */
export function grossProfitOf(l: PnlMonthLines): number {
  return l.revenue - l.cogs - l.taxes;
}

/** Los meses que abarca un período terminado en `month`. */
export function periodMonths(period: PnlPeriod, month: number): number[] {
  if (period === 'month') return [month];
  return Array.from({ length: month }, (_, i) => i + 1);
}

function findMonth(rows: PnlMonthLines[], month: number): PnlMonthLines | undefined {
  return rows.find((r) => r.month === month);
}

/** Suma las líneas de varios meses (los que no existen cuentan cero). */
export function sumLines(rows: PnlMonthLines[], months: number[]): PnlMonthLines {
  const out = emptyLines(months[months.length - 1] ?? 0);
  for (const m of months) {
    const r = findMonth(rows, m);
    if (!r) continue;
    out.revenue += r.revenue;
    out.cogs += r.cogs;
    out.gya += r.gya;
    out.investment += r.investment;
    out.bonos += r.bonos;
    out.taxes += r.taxes;
  }
  return out;
}

export function hasBudget(data: PnlDashboardData): boolean {
  return data.budget.length > 0;
}

/** Variación porcentual contra una base; null si la base es cero. */
export function pctDelta(value: number, base: number): number | null {
  if (!base) return null;
  return ((value - base) / Math.abs(base)) * 100;
}

/** true si la fila entra en el modo elegido ('total' = payroll + non-payroll). */
export function matchesPay(row: { kind: PnlPayKind }, pay: PnlPayMode): boolean {
  return pay === 'total' || row.kind === pay;
}

/**
 * Payroll de Cogs + G&A + Investment por mes (USD). No incluye Bonos anuales ni
 * Taxes: ya son líneas propias del P&L. Plan = payroll + other_payroll_impacts,
 * así payroll + non-payroll = la línea del P&L también en el BGT.
 */
export function payrollByMonth(data: PnlDashboardData, scenario: PnlScenario): Map<number, number> {
  const out = new Map<number, number>();
  for (const r of data.opex) {
    if (r.scenario !== scenario || r.kind !== 'payroll') continue;
    out.set(r.month, (out.get(r.month) ?? 0) + r.usd);
  }
  return out;
}

function sumMonths(byMonth: Map<number, number>, months: number[]): number {
  let s = 0;
  for (const m of months) s += byMonth.get(m) ?? 0;
  return s;
}

/* ───────────────────────── 01 · KPIs ───────────────────────── */

export type PnlKpiKey = 'revenue' | 'opex' | 'grossProfit' | 'grossMargin' | 'ebitda' | 'ebitdaPct' | 'payroll';

export interface PnlKpi {
  key: PnlKpiKey;
  /** true = más es mejor. */
  up: boolean;
  /** true = el valor es un porcentaje y las variaciones van en puntos. */
  pp: boolean;
  value: number;
  budget: number | null;
  prev: number | null;
  /** Serie mensual real (enero → último mes cerrado) para el sparkline. */
  series: number[];
}

function kpiValues(l: PnlMonthLines, payroll: number): Record<PnlKpiKey, number> {
  const gp = grossProfitOf(l);
  const eb = ebitdaOf(l);
  return {
    revenue: l.revenue,
    opex: opexOf(l),
    grossProfit: gp,
    grossMargin: l.revenue ? (gp / l.revenue) * 100 : 0,
    ebitda: eb,
    ebitdaPct: l.revenue ? (eb / l.revenue) * 100 : 0,
    payroll,
  };
}

const KPI_DEF: Array<{ key: PnlKpiKey; up: boolean; pp: boolean }> = [
  { key: 'revenue', up: true, pp: false },
  { key: 'opex', up: false, pp: false },
  { key: 'grossProfit', up: true, pp: false },
  { key: 'grossMargin', up: true, pp: true },
  { key: 'ebitda', up: true, pp: false },
  { key: 'ebitdaPct', up: true, pp: true },
  // Payroll de Cogs + G&A + Investment: antes no vivía en ninguna pantalla, solo suelto en el mensaje de cierre.
  { key: 'payroll', up: false, pp: false },
];

export function computeKpis(data: PnlDashboardData, month: number, period: PnlPeriod): PnlKpi[] {
  const months = periodMonths(period, month);
  const payA = payrollByMonth(data, 'actual');
  const payB = payrollByMonth(data, 'budget');
  const a = kpiValues(sumLines(data.actual, months), sumMonths(payA, months));
  const b = hasBudget(data) ? kpiValues(sumLines(data.budget, months), sumMonths(payB, months)) : null;
  const prev = period === 'month' && month > 1 ? kpiValues(sumLines(data.actual, [month - 1]), sumMonths(payA, [month - 1])) : null;
  const series = data.actual
    .filter((r) => r.month <= data.closedMonth)
    .sort((x, y) => x.month - y.month)
    .map((r) => kpiValues(r, payA.get(r.month) ?? 0));
  return KPI_DEF.map((d) => ({
    key: d.key,
    up: d.up,
    pp: d.pp,
    value: a[d.key],
    budget: b ? b[d.key] : null,
    prev: prev ? prev[d.key] : null,
    series: series.map((s) => s[d.key]),
  }));
}

/** Favorable si el desvío va en la dirección deseada (cero cuenta favorable). */
export function isFavorable(up: boolean, delta: number): boolean {
  return up ? delta >= 0 : delta <= 0;
}

/* ───────────────────────── 02 · Waterfall ───────────────────────── */

export interface PnlWaterfallStep {
  key: 'start' | PnlLine | 'end';
  value: number;
  total: boolean;
}

/** Puente EBITDA BGT → EBITDA real. Cada paso de costo va con el signo invertido: gastar menos mejora el EBITDA. */
export function computeWaterfall(data: PnlDashboardData, month: number, period: PnlPeriod): PnlWaterfallStep[] {
  const months = periodMonths(period, month);
  const a = sumLines(data.actual, months);
  const b = sumLines(data.budget, months);
  return [
    { key: 'start', value: ebitdaOf(b), total: true },
    { key: 'revenue', value: a.revenue - b.revenue, total: false },
    { key: 'cogs', value: -(a.cogs - b.cogs), total: false },
    { key: 'gya', value: -(a.gya - b.gya), total: false },
    { key: 'investment', value: -(a.investment - b.investment), total: false },
    { key: 'bonos', value: -(a.bonos - b.bonos), total: false },
    { key: 'taxes', value: -(a.taxes - b.taxes), total: false },
    { key: 'end', value: ebitdaOf(a), total: true },
  ];
}

/* ───────────────────────── 03 · Drivers ───────────────────────── */

export interface PnlDriverItem {
  label: string;
  /** actual − referencia (BGT o mes anterior), USD. */
  value: number;
  actual: number;
  ref: number;
  sub?: PnlDriverItem[];
}

export interface PnlDrivers {
  group: PnlDriverGroup;
  base: PnlBase;
  /** Qué parte de la línea se abrió (siempre 'nonPayroll' para revenue). */
  pay: PnlPayMode;
  /** Total real del período (según `pay` para las líneas de costo). */
  actual: number;
  ref: number;
  /** true = un desvío positivo deteriora el EBITDA (líneas de costo). */
  worsePos: boolean;
  items: PnlDriverItem[];
}

function byAbsDesc(a: { value: number }, b: { value: number }): number {
  return Math.abs(b.value) - Math.abs(a.value);
}

interface Keyed {
  scenario: PnlScenario;
  month: number;
  usd: number;
}

/** Agrega filas en dos niveles (grupo → detalle) y arma la diferencia real − referencia. */
function buildTree<R extends Keyed>(
  rows: R[],
  actualMonths: number[],
  refScenario: PnlScenario,
  refMonths: number[],
  groupOf: (r: R) => string,
  subOf: ((r: R) => string) | null
): PnlDriverItem[] {
  const acc = new Map<string, { a: number; r: number; sub: Map<string, { a: number; r: number }> }>();
  const touch = (g: string) => {
    let e = acc.get(g);
    if (!e) {
      e = { a: 0, r: 0, sub: new Map() };
      acc.set(g, e);
    }
    return e;
  };
  for (const row of rows) {
    const isActual = row.scenario === 'actual' && actualMonths.includes(row.month);
    const isRef = row.scenario === refScenario && refMonths.includes(row.month);
    if (!isActual && !isRef) continue;
    const e = touch(groupOf(row));
    if (isActual) e.a += row.usd;
    if (isRef) e.r += row.usd;
    if (subOf) {
      const k = subOf(row);
      const s = e.sub.get(k) ?? { a: 0, r: 0 };
      if (isActual) s.a += row.usd;
      if (isRef) s.r += row.usd;
      e.sub.set(k, s);
    }
  }
  const items: PnlDriverItem[] = [];
  for (const [label, e] of acc) {
    const item: PnlDriverItem = { label, value: e.a - e.r, actual: e.a, ref: e.r };
    if (subOf) {
      // Investment y G&A superan las 40 combinaciones: se muestran las 8 que más mueven; el padre suma todas.
      item.sub = [...e.sub]
        .map(([l, s]) => ({ label: l, value: s.a - s.r, actual: s.a, ref: s.r }))
        .filter((s) => Math.abs(s.value) >= 50 || Math.abs(s.actual) >= 50 || Math.abs(s.ref) >= 50)
        .sort(byAbsDesc)
        .slice(0, DRIVER_SUB_LIMIT);
    }
    items.push(item);
  }
  return items.filter((i) => Math.abs(i.value) >= 50 || Math.abs(i.actual) >= 50 || Math.abs(i.ref) >= 50).sort(byAbsDesc);
}

/**
 * Qué mueve la aguja de una línea. `base` 'prev' compara el mes contra el
 * anterior (solo período mes y month > 1); si no aplica, cae a BGT. Para las
 * líneas de costo, `pay` elige non-payroll (default), payroll o total; el
 * payroll aparece como la categoría 'S&B' de cada grupo.
 */
export function computeDrivers(
  data: PnlDashboardData,
  group: PnlDriverGroup,
  month: number,
  period: PnlPeriod,
  base: PnlBase,
  pay: PnlPayMode = 'nonPayroll'
): PnlDrivers {
  const effectiveBase: PnlBase = base === 'prev' && period === 'month' && month > 1 ? 'prev' : 'budget';
  const actualMonths = periodMonths(period, month);
  const refScenario: PnlScenario = effectiveBase === 'prev' ? 'actual' : 'budget';
  const refMonths = effectiveBase === 'prev' ? [month - 1] : actualMonths;

  let items: PnlDriverItem[];
  let effectivePay: PnlPayMode = pay;
  if (group === 'revenue') {
    effectivePay = 'nonPayroll';
    items = buildTree(data.revenue, actualMonths, refScenario, refMonths, (r) => r.solution, null);
  } else {
    const rows = data.opex.filter((r) => r.line === group && matchesPay(r, pay));
    items = buildTree(
      rows,
      actualMonths,
      refScenario,
      refMonths,
      (r) => r.group,
      // Investment: "categoría · business case"; Cogs y G&A: la categoría (el payroll es 'S&B').
      group === 'investment' ? (r) => `${r.category} · ${shortBusinessCase(r.costCenter)}` : (r) => r.category
    );
  }
  const actual = items.reduce((s, i) => s + i.actual, 0);
  const ref = items.reduce((s, i) => s + i.ref, 0);
  return { group, base: effectiveBase, pay: effectivePay, actual, ref, worsePos: group !== 'revenue', items };
}

/* ───────────────────────── 03b · FX ───────────────────────── */

export interface PnlFxItem {
  company: string;
  month: number;
  prev: number;
  ytd: number;
}

/** Impacto FX por sociedad: el mes, el anterior y el acumulado del año. Sociedades sin movimiento no aparecen. */
export function computeFx(data: PnlDashboardData, month: number): PnlFxItem[] {
  const acc = new Map<string, PnlFxItem>();
  for (const r of data.fx) {
    const e = acc.get(r.company) ?? { company: r.company, month: 0, prev: 0, ytd: 0 };
    if (r.month === month) e.month += r.usd;
    if (r.month === month - 1) e.prev += r.usd;
    if (r.month <= month) e.ytd += r.usd;
    acc.set(r.company, e);
  }
  return [...acc.values()]
    .filter((e) => Math.abs(e.month) >= 50 || Math.abs(e.prev) >= 50 || Math.abs(e.ytd) >= 50)
    .sort((a, b) => a.month - b.month);
}

/* ───────────────────────── 04/05 · COGS e Investment (lista + detalle) ───────────────────────── */

export interface PnlBreakdownCategory {
  category: string;
  actual: number;
  plan: number;
  /** 'var': real − plan (desvío). 'run': plan − real (saldo). */
  amount: number;
  pct: number | null;
}

export interface PnlBreakdownCase {
  /** `${group}|${name}`: la misma Solución aparece en Direct y en Indirect Cost como dos filas. */
  id: string;
  /** Cohorte / tipo de costo: New Business · Same Store (Investment), Direct · Indirect Cost (Cogs). */
  group: string;
  /** Business case (Investment) o Solución (Cogs). */
  name: string;
  short: string;
  actual: number;
  plan: number;
  amount: number;
  pct: number | null;
  categories: PnlBreakdownCategory[];
}

export interface PnlBreakdownView {
  line: PnlCostLine;
  pay: PnlPayMode;
  /** 'var' = el plan del período ya terminó de correr (dólares = desvío). 'run' = sigue corriendo (dólares = saldo). */
  mode: 'var' | 'run';
  actualMonths: number[];
  planMonths: number[];
  actual: number;
  plan: number;
  amount: number;
  pct: number | null;
  cases: PnlBreakdownCase[];
  /** Todos los grupos de la línea (sin aplicar el filtro), para armar el selector. */
  groups: string[];
}

export interface PnlBreakdownOptions {
  line: PnlCostLine;
  month: number;
  period: PnlBreakdownPeriod;
  pay?: PnlPayMode;
  /** Un grupo exacto, o un predicado; sin filtro = todos. */
  group?: string | ((group: string) => boolean);
}

/** Meses del plan y del real para cada período, con `month` como mes de referencia. */
export function breakdownMonths(period: PnlBreakdownPeriod, month: number): { actualMonths: number[]; planMonths: number[] } {
  if (period === 'month') return { actualMonths: [month], planMonths: [month] };
  if (period === 'ytd') {
    const ms = periodMonths('ytd', month);
    return { actualMonths: ms, planMonths: ms };
  }
  if (period === 'semester') {
    const start = month <= 6 ? 1 : 7;
    const planMonths = Array.from({ length: 6 }, (_, i) => start + i);
    return { actualMonths: planMonths.filter((m) => m <= month), planMonths };
  }
  const planMonths = Array.from({ length: 12 }, (_, i) => i + 1);
  return { actualMonths: planMonths.filter((m) => m <= month), planMonths };
}
/** "Content Acquisitions SouthCone" → "Content Acq. SouthCone", para que entre en la lista. */
export function shortBusinessCase(name: string): string {
  return name.replace(/\bAcquisitions\b/g, 'Acq.').replace(/\bInternational\b/g, 'Intl.');
}

/** Cohorte de Investment a partir del grupo (CC_Reporting / PL2): "Same Store" → SS, el resto NB. */
export function cohortOf(group: string): PnlCohort {
  return group.toLowerCase().replace(/\s+/g, '').startsWith('same') ? 'SS' : 'NB';
}

/** Sigla del grupo para el chip de la lista: "Direct Cost" → "DC", "New Business" → "NB". */
export function groupCode(group: string): string {
  const words = group.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (words.length >= 2) return words.map((w) => w[0]).join('').toUpperCase().slice(0, 3);
  return group.slice(0, 2).toUpperCase();
}

/**
 * Lista rankeada + detalle de una línea de costo: Investment por business case
 * (cohorte NB / Same Store) y COGS por Solución (Direct / Indirect Cost). Mes y
 * acumulado comparan contra el plan de esos mismos meses (dólares = desvío,
 * % = ejecución); semestre y año contra un plan que sigue corriendo (dólares =
 * saldo, % = consumido). El orden es siempre por dólares, nunca por porcentaje.
 */
export function computeBreakdown(data: PnlDashboardData, opts: PnlBreakdownOptions): PnlBreakdownView {
  const pay = opts.pay ?? 'nonPayroll';
  const { actualMonths, planMonths } = breakdownMonths(opts.period, opts.month);
  const mode: 'var' | 'run' = planMonths.every((m) => actualMonths.includes(m)) ? 'var' : 'run';
  const amountOf = (a: number, p: number) => (mode === 'var' ? a - p : p - a);
  const pctOf = (a: number, p: number) => (p ? (a / p) * 100 : null);
  const keep = typeof opts.group === 'function' ? opts.group : opts.group ? (g: string) => g === opts.group : () => true;

  const groups = new Set<string>();
  const acc = new Map<string, { group: string; name: string; a: number; p: number; cats: Map<string, { a: number; p: number }> }>();
  for (const r of data.opex) {
    if (r.line !== opts.line) continue;
    groups.add(r.group);
    if (!matchesPay(r, pay) || !keep(r.group)) continue;
    const isA = r.scenario === 'actual' && actualMonths.includes(r.month);
    const isP = r.scenario === 'budget' && planMonths.includes(r.month);
    if (!isA && !isP) continue;
    const id = `${r.group}|${r.costCenter}`;
    let e = acc.get(id);
    if (!e) {
      e = { group: r.group, name: r.costCenter, a: 0, p: 0, cats: new Map() };
      acc.set(id, e);
    }
    const c = e.cats.get(r.category) ?? { a: 0, p: 0 };
    if (isA) {
      e.a += r.usd;
      c.a += r.usd;
    }
    if (isP) {
      e.p += r.usd;
      c.p += r.usd;
    }
    e.cats.set(r.category, c);
  }

  const cases: PnlBreakdownCase[] = [...acc]
    .map(([id, e]) => ({
      id,
      group: e.group,
      name: e.name,
      short: shortBusinessCase(e.name),
      actual: e.a,
      plan: e.p,
      amount: amountOf(e.a, e.p),
      pct: pctOf(e.a, e.p),
      categories: [...e.cats]
        .map(([category, c]) => ({ category, actual: c.a, plan: c.p, amount: amountOf(c.a, c.p), pct: pctOf(c.a, c.p) }))
        .filter((c) => Math.abs(c.actual) >= 50 || Math.abs(c.plan) >= 50)
        .sort((x, y) => Math.abs(y.amount) - Math.abs(x.amount)),
    }))
    // siempre por dólares, nunca por porcentaje
    .sort((x, y) => Math.abs(y.amount) - Math.abs(x.amount));

  const actual = cases.reduce((s, c) => s + c.actual, 0);
  const plan = cases.reduce((s, c) => s + c.plan, 0);
  return {
    line: opts.line,
    pay,
    mode,
    actualMonths,
    planMonths,
    actual,
    plan,
    amount: amountOf(actual, plan),
    pct: pctOf(actual, plan),
    cases,
    groups: [...groups].sort(),
  };
}

/** Business case de Investment con los nombres de siempre (lo usa el digest del Brain). */
export interface PnlInvestmentCase extends PnlBreakdownCase {
  businessCase: string;
  cohort: PnlCohort;
}

export interface PnlInvestmentView extends Omit<PnlBreakdownView, 'cases'> {
  cases: PnlInvestmentCase[];
}

/** Investment non-payroll por business case: la primera instancia del motor, con la cohorte NB / SS resuelta. */
export function computeInvestment(
  data: PnlDashboardData,
  month: number,
  period: PnlBreakdownPeriod,
  cohort: PnlCohort | 'all'
): PnlInvestmentView {
  const v = computeBreakdown(data, { line: 'investment', month, period, pay: 'nonPayroll', group: cohort === 'all' ? undefined : (g) => cohortOf(g) === cohort });
  return { ...v, cases: v.cases.map((c) => ({ ...c, businessCase: c.name, cohort: cohortOf(c.group) })) };
}

/* ───────────────────────── 05 · Tabla ───────────────────────── */

export type PnlTableRowKey =
  | PnlLine
  | 'grossRevenue'
  | 'mediaInvestment'
  | 'grossProfit'
  | 'ebitda'
  | 'otherIncomes'
  | 'existencial'
  | 'otherExpenses'
  | 'ebitdaFinal'
  | 'interestExpense'
  | 'resultAfterInterest';

export interface PnlTableRow {
  key: PnlTableRowKey;
  up: boolean;
  total: boolean;
  /** Fila below EBITDA (estilo tenue). */
  below: boolean;
  /** Línea separadora arriba (el primer below EBITDA y Interest expense). */
  sep: boolean;
  actual: number;
  budget: number | null;
  prev: number | null;
  ytdActual: number;
  ytdBudget: number | null;
}

function belowOf(data: PnlDashboardData, months: number[]): { otherIncomes: number; existencial: number; otherExpenses: number } {
  const out = { otherIncomes: 0, existencial: 0, otherExpenses: 0 };
  for (const r of data.below) {
    if (!months.includes(r.month)) continue;
    out.otherIncomes += r.otherIncomes;
    out.existencial += r.existencial;
    out.otherExpenses += r.otherExpenses;
  }
  return out;
}

export function ebitdaFinalOf(l: PnlMonthLines, b: { otherIncomes: number; existencial: number; otherExpenses: number }): number {
  return ebitdaOf(l) + b.otherIncomes - b.existencial - b.otherExpenses;
}

function extraOf(data: PnlDashboardData, months: number[]): { mediaInvestment: number; interestExpense: number } {
  const out = { mediaInvestment: 0, interestExpense: 0 };
  for (const r of data.extra ?? []) {
    if (!months.includes(r.month)) continue;
    out.mediaInvestment += r.mediaInvestment;
    out.interestExpense += r.interestExpense;
  }
  return out;
}

export function computeTable(data: PnlDashboardData, month: number): PnlTableRow[] {
  const withBudget = hasBudget(data);
  const ytd = periodMonths('ytd', month);
  const a = sumLines(data.actual, [month]);
  const b = sumLines(data.budget, [month]);
  const p = month > 1 ? sumLines(data.actual, [month - 1]) : null;
  const ya = sumLines(data.actual, ytd);
  const yb = sumLines(data.budget, ytd);
  const bl = belowOf(data, [month]);
  const blp = month > 1 ? belowOf(data, [month - 1]) : null;
  const bly = belowOf(data, ytd);
  const ex = extraOf(data, [month]);
  const exp = month > 1 ? extraOf(data, [month - 1]) : null;
  const exy = extraOf(data, ytd);

  const line = (key: PnlLine, up: boolean): PnlTableRow => ({
    key,
    up,
    total: false,
    below: false,
    sep: false,
    actual: a[key],
    budget: withBudget ? b[key] : null,
    prev: p ? p[key] : null,
    ytdActual: ya[key],
    ytdBudget: withBudget ? yb[key] : null,
  });
  const calc = (key: PnlTableRowKey, f: (l: PnlMonthLines) => number): PnlTableRow => ({
    key,
    up: true,
    total: true,
    below: false,
    sep: false,
    actual: f(a),
    budget: withBudget ? f(b) : null,
    prev: p ? f(p) : null,
    ytdActual: f(ya),
    ytdBudget: withBudget ? f(yb) : null,
  });
  const below = (key: 'otherIncomes' | 'existencial' | 'otherExpenses', up: boolean): PnlTableRow => ({
    key,
    up,
    total: false,
    below: true,
    sep: key === 'otherIncomes',
    actual: bl[key],
    budget: null,
    prev: blp ? blp[key] : null,
    ytdActual: bly[key],
    ytdBudget: null,
  });

  /** Filas sin plan (el presupuesto de la compañía está armado sobre revenue neto). */
  const noPlan = (key: PnlTableRowKey, o: { up: boolean; total: boolean; below: boolean; sep?: boolean }, actual: number, prev: number | null, ytdActual: number): PnlTableRow => ({
    key,
    up: o.up,
    total: o.total,
    below: o.below,
    sep: !!o.sep,
    actual,
    budget: null,
    prev,
    ytdActual,
    ytdBudget: null,
  });
  const efa = ebitdaFinalOf(a, bl);
  const efp = p && blp ? ebitdaFinalOf(p, blp) : null;
  const efy = ebitdaFinalOf(ya, bly);

  return [
    noPlan('grossRevenue', { up: true, total: true, below: false }, a.revenue + ex.mediaInvestment, p && exp ? p.revenue + exp.mediaInvestment : null, ya.revenue + exy.mediaInvestment),
    noPlan('mediaInvestment', { up: false, total: false, below: false }, ex.mediaInvestment, exp ? exp.mediaInvestment : null, exy.mediaInvestment),
    line('revenue', true),
    line('cogs', false),
    line('taxes', false),
    calc('grossProfit', grossProfitOf),
    line('gya', false),
    line('investment', false),
    line('bonos', false),
    calc('ebitda', ebitdaOf),
    below('otherIncomes', true),
    below('existencial', false),
    below('otherExpenses', false),
    noPlan('ebitdaFinal', { up: true, total: true, below: true }, efa, efp, efy),
    noPlan('interestExpense', { up: false, total: false, below: true, sep: true }, ex.interestExpense, exp ? exp.interestExpense : null, exy.interestExpense),
    noPlan('resultAfterInterest', { up: true, total: true, below: true }, efa - ex.interestExpense, efp !== null && exp ? efp - exp.interestExpense : null, efy - exy.interestExpense),
  ];
}

/* ───────────────────────── 06 · Heatmap ───────────────────────── */

export interface PnlHeatmapRow {
  key: PnlLine | 'ebitdaPct';
  /** Desvío favorable (+) / desfavorable (−) por mes: % para las líneas, puntos para % EBITDA. */
  cells: Array<number | null>;
}

export function computeHeatmap(data: PnlDashboardData): PnlHeatmapRow[] {
  const months = periodMonths('ytd', data.closedMonth);
  const cell = (m: number, f: (a: PnlMonthLines, b: PnlMonthLines) => number | null): number | null => {
    const a = findMonth(data.actual, m);
    const b = findMonth(data.budget, m);
    if (!a || !b) return null;
    return f(a, b);
  };
  const linePct = (key: PnlLine, up: boolean) => (a: PnlMonthLines, b: PnlMonthLines) => {
    const d = pctDelta(a[key], b[key]);
    return d === null ? null : up ? d : -d;
  };
  const rows: PnlHeatmapRow[] = [
    { key: 'revenue', cells: months.map((m) => cell(m, linePct('revenue', true))) },
    { key: 'cogs', cells: months.map((m) => cell(m, linePct('cogs', false))) },
    { key: 'gya', cells: months.map((m) => cell(m, linePct('gya', false))) },
    { key: 'investment', cells: months.map((m) => cell(m, linePct('investment', false))) },
    { key: 'bonos', cells: months.map((m) => cell(m, linePct('bonos', false))) },
    { key: 'taxes', cells: months.map((m) => cell(m, linePct('taxes', false))) },
    {
      key: 'ebitdaPct',
      cells: months.map((m) =>
        cell(m, (a, b) => (a.revenue && b.revenue ? (ebitdaOf(a) / a.revenue) * 100 - (ebitdaOf(b) / b.revenue) * 100 : null))
      ),
    },
  ];
  return rows;
}
